import { useCallback, useEffect, useState, type ReactNode } from 'react';
import AuthenticationPage from './AuthenticationPage';
import { api } from '@/shared/axios-client';
import {
  AUTH_UNAUTHORIZED_EVENT,
  isAuthenticated,
  saveAuthSession,
  type AuthSessionData,
} from '@/shared/auth-session';

interface LoginApiResponse {
  status: string;
  message: string;
  data: AuthSessionData;
}

interface AccessGateProps {
  children: ReactNode;
}

/**
 * Renders the authentication page until the user signs in via the backend.
 */
const AccessGate = ({ children }: AccessGateProps) => {
  const [unlocked, setUnlocked] = useState(() => isAuthenticated());

  useEffect(() => {
    const onUnauthorized = () => {
      setUnlocked(false);
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, []);

  const handleLogin = useCallback(
    async (username: string, password: string, remember = false) => {
      try {
        const response = (await api.post('/api/auth/login', {
          username,
          password,
        })) as LoginApiResponse;

        if (!response?.data?.access_token) {
          return false;
        }

        saveAuthSession(response.data, remember);
        setUnlocked(true);
        return true;
      } catch {
        return false;
      }
    },
    [],
  );

  if (!unlocked) {
    return <AuthenticationPage onSubmit={handleLogin} />;
  }

  return <>{children}</>;
};

export default AccessGate;
